import { useSearchParams } from 'react-router-dom'
import { useRef, useState } from 'react'
import mirapassLogo from '../assets/landing/logo-mirapass.png'
import heroPhoto from '../assets/landing/hero-photo.jpg'
import galGroupInquiry from '../assets/landing/gal-group-inquiry.jpg'
import galVideo from '../assets/landing/gal-video.jpg'
import galMiraiWork from '../assets/landing/gal-mirai-work.jpg'
import galWorkshop from '../assets/landing/gal-workshop.jpg'
import galClassroom from '../assets/landing/gal-classroom.jpg'
import shotTeacherList from '../assets/landing/shot-teacher-list.jpg'
import shotTeacherDetail from '../assets/landing/shot-teacher-detail.jpg'
import voiceSchool from '../assets/landing/voice-school.jpg'
import GoogleOneTap from '../components/GoogleOneTap'
import TermsDialog from '../components/TermsDialog'
import LoginDialog from '../components/LoginDialog'
import SiteHeader from '../components/landing/SiteHeader'
import type { NavItem } from '../components/landing/SiteHeader'
import {
  useSmoothScroll, useHeaderOnLight, useParallax, useManifesto,
  useScrollGallery, useCountUp, useReveal, useStickyCta,
} from '../components/landing/effects'
import { useRedirectIfSignedIn } from '../hooks/useRedirectIfSignedIn'
import './landingSite.css'
import './LandingPage.css'

const CONTACT_URL = 'https://mirasso.co.jp/contact'
const COMPANY_URL = 'https://mirasso.co.jp/company'

const NAV_ITEMS: NavItem[] = [
  { href: '#problem', label: '課題' },
  { href: '#product', label: 'プロダクト' },
  { href: '#teacher', label: '先生向け' },
  { href: '#curriculum', label: 'カリキュラム' },
  { href: '#proof', label: '導入実績' },
  { href: '#price', label: '料金' },
  { href: '/entre', label: 'アントレ部' },
  { href: COMPANY_URL, label: '会社概要', external: true },
]

const MANIFESTO = [
  { t: '探究は、' }, { t: 'テストの点数じゃない。' }, { t: '自分の「なりたい」に、' }, { t: '自分の力で近づいていく時間。' },
  { t: 'その一歩ひとりひとりに、' }, { t: 'AIが寄り添い、' }, { t: '先生は、' }, { t: 'まっすぐ生徒と向き合える。' },
  { t: '未来をWakuWakuさせる学びを、', accent: true }, { t: 'すべての教室へ。', accent: true },
]

const GALLERY = [
  { img: galGroupInquiry, tab: '教室でのグループ探究', title: '個人の思考と、チームの対話が往復する。', caption: '教室でのグループ探究', alt: '生徒たちがグループでMiraPASSの画面を囲み、話し合いながら探究を進める様子', eager: true },
  { img: galVideo, tab: '動画教材', title: '約10分の動画が、探究の入口になる。', caption: '動画教材', alt: 'MiraPASSの動画教材。講師が本日のポイントを解説し、みらいワークと連動する' },
  { img: galMiraiWork, tab: 'AIみらいワーク', title: '答えを教えず、問いで深めるAI。', caption: 'AIみらいワーク', alt: 'AIみらいワークのチャット画面。問い返し形式で生徒の考えを引き出す' },
  { img: galWorkshop, tab: '全国合同ワークショップ', title: '月1回、全国の仲間とつながる。', caption: 'アントレ部 合同ワークショップ', alt: '全国の学校の生徒がオンラインでつながる、月1回の合同ワークショップの画面' },
  { img: galClassroom, tab: '導入校の授業', title: 'スクリーンにも、手元にも、MiraPASS。', caption: 'かほく市立高松中学校', alt: 'かほく市立高松中学校の授業風景。スクリーンにMiraPASSの動画が映り、生徒が端末で学ぶ' },
]

const TICKER_1 = [
  ['行政', '宮城県教育委員会'],
  ['行政', '東京都 Tokyo Startup Gateway'],
  ['行政', '東京都渋谷区'],
  ['高専', '釧路高専（北海道）'],
  ['高専', '豊田高専（愛知）'],
  ['公立', '京都府立洛東高等学校'],
  ['公立', 'かほく市立高松中学校（石川）'],
]

const TICKER_2 = [
  ['公立', '奈良市立都南中学校'],
  ['公立', '大泉町立南中学校（群馬）'],
  ['私立', '早稲田大阪高等学校'],
  ['私立', '岩倉高等学校（東京）'],
  ['私立', 'かえつ有明中・高等学校'],
  ['私立', '明治大学付属八王子中学・高等学校'],
  ['私立', '明治大学付属中野中学・高等学校'],
  ['私立', '宇都宮短期大学附属高等学校'],
  ['私立', '文星芸術大学附属中学校・高等学校'],
]

const JOURNEY = [
  { no: '01', title: '課題の設定', desc: '動画とAIワークで、自分ごとの問いを立てる' },
  { no: '02', title: '情報の収集・整理・分析', desc: 'AIとの対話で、考えを広げ、深める' },
  { no: '03', title: 'まとめ・表現', desc: '探究の成果を、自分の言葉でアウトプット' },
  { no: '04', title: '実装', tag: 'AI制作', desc: 'アプリ・動画・絵本など、AIを活用して形にする' },
  { no: '05', title: '挑戦', tag: 'アントレ部', desc: '探究コンテストや、他校の生徒との共創に挑む' },
  { no: '06', title: '進路へ', desc: '探究の成果が、生徒・先生・学校に見える形で残る' },
]

const FAQ = [
  { q: '探究の指導経験がなくても使えますか？', a: 'はい。課題の設定からまとめ・表現まで、標準的な探究プロセスのコンテンツをすべて用意しています。授業は「動画×AIワーク×ディスカッション」の型で進められ、導入から運用までミラッソが伴走します。' },
  { q: 'AIが答えを教えてしまい、考える力が育たないのでは？', a: 'みらいワークは探究用にカスタマイズされており、通常のAIのように答えを教えません。問い返し形式で生徒の思考を広げ、深める設計です。安心・安全にも配慮しています。' },
  { q: '先生の負担はどれくらい減りますか？', a: '動画・ワーク・教材が用意されているため授業準備の時間を減らせます。さらに生徒の状況が可視化されるので、困っている生徒への個別サポートに時間を使えます。教育課程に合わせた教材制作の支援も可能です。' },
  { q: 'DXハイスクールでの活用はできますか？', a: 'はい、DXハイスクールに対応しています。AIの基礎からアプリ・動画・絵本の制作まで、AIを活用した特色ある学びを設計できます。詳細は資料でご案内します。' },
  { q: '年度の途中からでも導入できますか？', a: '学年・時期・コマ数に合わせた設計が可能です。まずは現状とご希望をお聞かせください。最適な導入の形をご提案します。' },
]

const CheckIcon = ({ size, color }: { size: number; color: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" stroke={color} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const LandingPage = () => {
  const [searchParams] = useSearchParams()
  const shouldOpenLogin = searchParams.get('login') === 'true'
  const returnUrl = searchParams.get('returnUrl')
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false)
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(shouldOpenLogin)
  const rootRef = useRef<HTMLDivElement>(null)

  useRedirectIfSignedIn(returnUrl)
  useSmoothScroll()
  useHeaderOnLight(rootRef, '.problem,.teacher,.proof,.price,.voice-sec,.faq-sec,.entrebu')
  useParallax(rootRef)
  useManifesto(rootRef)
  useScrollGallery(rootRef)
  useCountUp(rootRef)
  useReveal(rootRef)
  useStickyCta(rootRef)

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID

  return (
    <>
      <div className="mp-site mp-home" ref={rootRef}>
        <SiteHeader logoHref="#top" navItems={NAV_ITEMS} onLogin={() => setIsLoginDialogOpen(true)} />

        {/* ============ HERO ============ */}
        <section className="hero" id="top">
          <div className="heroPhoto" aria-hidden="true" style={{ backgroundImage: `url(${heroPhoto})` }} />
          <div className="heroVeil" aria-hidden="true" />
          <div className="aurora" aria-hidden="true" />
          <div className="grain" aria-hidden="true" />
          <div className="wrap">
            <span className="chip rise d1"><span className="dot"></span><span>AI探究プラットフォーム{'　·　'}DXハイスクール対応</span></span>
            <h1 className="headline rise d2">探究に、<br /><span className="shimmer">一人ひとりのAI伴走を。</span></h1>
            <p className="lead rise d3">MiraPASSは、動画とAI「みらいワーク」で、生徒一人ひとりの探究に寄り添うプラットフォーム。先生の授業準備を減らし、生徒の「考える力」を育てます。</p>
            <p className="hero-note rise d5">先生アカウント無料{'　·　'}導入から運用まで伴走</p>
            <div className="scroll-cue" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#8e8e93" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
          </div>
        </section>

        {/* ============ GALLERY ============ */}
        <section className="gal" id="gallery">
          <div className="gal-sticky">
            <div className="wrap">
              <div className="gal-tabs reveal" role="tablist" aria-label="ギャラリーの切り替え">
                {GALLERY.map((g, i) => (
                  <button key={g.tab} type="button" className={`gal-tab${i === 0 ? ' on' : ''}`}>{g.tab}</button>
                ))}
              </div>
            </div>
            <div className="gal-track">
              {GALLERY.map((g, i) => (
                <div key={g.tab} className={`gal-card${i === 0 ? ' active' : ''}`}>
                  <img src={g.img} alt={g.alt} loading={g.eager ? 'eager' : 'lazy'} fetchPriority={g.eager ? 'high' : undefined} />
                  <div className="cap"><b>{g.title}</b><span>{g.caption}</span></div>
                </div>
              ))}
            </div>
            <div className="gal-dots" aria-hidden="true">
              {GALLERY.map(g => <button key={g.tab} type="button" className="gal-dot" />)}
            </div>
          </div>
        </section>

        {/* ============ STATS ============ */}
        <section className="stats">
          <div className="grain" aria-hidden="true" />
          <div className="wrap">
            <div className="row stagger">
              <div className="stat"><div className="n grad-text"><span className="cnt" data-to="29">0</span><small>校</small></div><div className="l">行政・高専・公立・私立で導入</div></div>
              <div className="stat"><div className="n grad-text"><span className="cnt" data-to="2143" data-comma="1">0</span><small>名</small></div><div className="l">サービスを届けた先生・生徒</div></div>
              <div className="stat"><div className="n grad-text">¥<span className="cnt" data-to="550">0</span><small>／月・生徒（税込）</small></div><div className="l">全機能が使える教材費モデル</div></div>
            </div>
          </div>
        </section>

        {/* ============ MANIFESTO ============ */}
        <section className="manifesto" aria-label="ミラッソの想い">
          <div className="stickybox">
            <div className="mglow" aria-hidden="true" />
            <p className="mtext">
              {MANIFESTO.map((p, i) => (
                <span key={p.t}>
                  {i > 0 && ' '}
                  <span className={`w${p.accent ? ' accent' : ''}`}>{p.t}</span>
                </span>
              ))}
            </p>
          </div>
        </section>

        {/* ============ SCENE：全幅の利用シーン ============ */}
        <section className="scene reveal" aria-label="導入教室の風景">
          <img src={heroPhoto} alt="教室でノートPCに向かい、MiraPASSで探究に取り組む生徒たち" loading="lazy" data-parallax />
          <div className="veil" aria-hidden="true" />
          <div className="cap">
            <div className="wrap">
              <span className="chip2">導入教室の風景</span>
              <h3>教室で、いま使われています。</h3>
            </div>
          </div>
        </section>

        {/* ============ PROBLEM ============ */}
        <section className="sec problem" id="problem">
          <div className="wrap">
            <div className="reveal">
              <p className="sec-kicker">学校が抱える課題</p>
              <h2 className="sec-title">探究の理想と、現場の現実。</h2>
              <p className="sec-sub">MiraPASSは、学校現場でよく伺う3つの課題から設計されています。</p>
            </div>
            <div className="prob-cards stagger">
              <div className="prob-card"><span className="no">01</span><h3>生徒一人ひとりの探究のサポートが大変</h3><p>テーマも進度もバラバラな探究。一斉授業では、一人ひとりに寄り添いきれない。</p></div>
              <div className="prob-card"><span className="no">02</span><h3>AIを活用した特色ある学びを実現したい</h3><p>AI時代の学びを取り入れたい。でも、何から始めればいいか分からない。</p></div>
              <div className="prob-card"><span className="no">03</span><h3>生徒が挑戦できる場を提供したい</h3><p>探究を校内の発表で終わらせず、コンテストや実社会への挑戦につなげたい。</p></div>
            </div>
            <p className="prob-bridge reveal">その3つに、<span className="grad-text">1つのプラットフォーム</span>で応える。</p>
          </div>
        </section>

        {/* ============ PRODUCT SHOWCASE ============ */}
        <section className="showcase" id="product">
          <div className="glowbg" aria-hidden="true" />
          <div className="grain" aria-hidden="true" />
          <div className="wrap">
            <div className="reveal">
              <p className="sec-kicker">プロダクト</p>
              <h2 className="sec-title">動画で学び、AIと深める。</h2>
              <p className="sec-sub">インプットは約10分の動画、アウトプットはAI「みらいワーク」との対話。授業の中で「考える → 言葉にする」のサイクルが回ります。</p>
            </div>

            <div className="feature reveal">
              <h3>集中が続く、約10分の講義動画。</h3>
              <p className="lead2">生徒の課題や興味に沿ったテーマを、起業家などの実践者が語ります。1テーマ約10分だから、授業に組み込みやすい。</p>
              <div className="pts">
                <span className="pt"><span className="ic"><CheckIcon size={10} color="#7fb0ff" /></span><span>実践者による講義</span></span>
                <span className="pt"><span className="ic"><CheckIcon size={10} color="#7fb0ff" /></span><span>約10分で1テーマ</span></span>
              </div>
              <div className="shot-big">
                <div className="glow" aria-hidden="true" />
                <img src={galVideo} alt="MiraPASSの動画教材。本日のポイントを講師が解説し、みらいワークと連動する画面" loading="lazy" />
              </div>
            </div>

            <div className="feature reveal">
              <h3>答えを教えないAI、「みらいワーク」。</h3>
              <p className="lead2">動画と連動したワークを、AIとの対話で言語化。探究用にカスタマイズされたAIが、問い返しながら生徒の考えを引き出します。</p>
              <div className="pts">
                <span className="pt"><span className="ic"><CheckIcon size={10} color="#7fb0ff" /></span><span>問い返し形式</span></span>
                <span className="pt"><span className="ic"><CheckIcon size={10} color="#7fb0ff" /></span><span>安心・安全に配慮</span></span>
                <span className="pt"><span className="ic"><CheckIcon size={10} color="#7fb0ff" /></span><span>AI制作にも広がる</span></span>
              </div>
              <div className="shot-big">
                <div className="glow" aria-hidden="true" />
                <img src={galMiraiWork} alt="AIみらいワークの対話画面。AIが生徒の考えを問い返しながら引き出す" loading="lazy" />
              </div>
            </div>

            <div className="feature reveal">
              <h3>授業は、動画 × AIワーク × 対話で。</h3>
              <p className="lead2">50分の授業を「動画インプット → AIワークで言語化 → グループディスカッション」の流れで設計。個人の思考とチームの対話が、自然に往復します。</p>
              <div className="pts">
                <span className="pt"><span className="ic"><CheckIcon size={10} color="#7fb0ff" /></span><span>そのまま使える授業モデル</span></span>
                <span className="pt"><span className="ic"><CheckIcon size={10} color="#7fb0ff" /></span><span>1人1台端末にフィット</span></span>
              </div>
              <div className="shot-big">
                <div className="glow" aria-hidden="true" />
                <img src={galGroupInquiry} alt="授業中、生徒たちがグループでMiraPASSの画面を囲みながら話し合う様子" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ============ TEACHER ============ */}
        <section className="sec teacher" id="teacher">
          <div className="wrap">
            <div className="center reveal">
              <p className="sec-kicker">先生向け</p>
              <h2 className="sec-title">クラス全体も、一人ひとりも、<br />ひと目で分かる。</h2>
              <p className="sec-sub">生徒のAIワークの状況から、先生は生徒の「今」を把握。一斉授業では見えにくかった、困っている生徒に手を差し伸べられます。</p>
            </div>
            <div className="t-big reveal">
              <h3>生徒の状況が、天気マークで分かる。</h3>
              <p>生徒一覧では、一人ひとりの状況が天気マークと要約で表示。クラス全体の傾向もAIが分析・要約するので、授業づくりのヒントになります。</p>
              <div className="lshot">
                <img src={shotTeacherList} alt="生徒一覧画面。生徒ごとの状況が天気マークと要約で表示される" loading="lazy" />
              </div>
            </div>
            <div className="t-split reveal">
              <div>
                <h3>一人ひとりの「良い点・課題・支援方針」まで。</h3>
                <p>生徒個別の画面では、最新の状況に加えて、その生徒の良い点、課題、支援方針を確認可能。一斉授業では見えにくかった、困っている生徒に手を差し伸べられます。カリキュラムに合わせた資料映像・ワークシートの作成支援など、先生向けサポートも充実。</p>
                <div className="zero-row">
                  <span className="zero-big">¥0</span>
                  <span style={{ color: 'var(--gray)', fontSize: '.9rem', fontWeight: 600 }}>先生アカウントは何人でも無料</span>
                </div>
              </div>
              <div className="lshot">
                <img src={shotTeacherDetail} alt="生徒個別画面。最新の状況と良い点・課題・支援方針が表示される" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ============ CURRICULUM ============ */}
        <section className="journey" id="curriculum">
          <div className="grain" aria-hidden="true" />
          <div className="wrap">
            <div className="reveal">
              <p className="sec-kicker">標準カリキュラム</p>
              <h2 className="sec-title">課題の設定から、挑戦・進路まで。</h2>
              <p className="sec-sub">標準的な探究プロセスのコンテンツをすべて用意。発表で終わらせず、実装・挑戦・進路につなげます。意欲的な生徒には、全国の生徒とつながる「アントレ部」も（標準プランに含まれます）。</p>
            </div>
            <div className="j-steps stagger">
              {JOURNEY.map(step => (
                <div className="j-step" key={step.no}>
                  <div className="num">{step.no}</div>
                  <div className="body">
                    <b>
                      <span>{step.title}</span>
                      {step.tag && <>{'　'}<span className="yr">{step.tag}</span></>}
                    </b>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="j-note"><b>授業は「動画インプット × AIワーク × グループディスカッション」で設計。</b>学校の教育課程や生徒の実態に合わせた、オリジナル教材の制作も可能です（オプション）。※一部コンテンツは制作中です。内容は変更の可能性があります。</p>
          </div>
        </section>

        {/* ============ ENTRE-BU（サマリー） ============ */}
        <section className="sec entrebu" id="entrebu">
          <div className="wrap">
            <div className="reveal">
              <p className="sec-kicker">MiraPASSの、その先へ</p>
              <h2 className="sec-title">意欲的な生徒には、<br />「アントレ部」も。</h2>
            </div>
            <div className="entrebu-grid reveal">
              <div className="entrebu-photo">
                <img src={galWorkshop} alt="全国の学校の生徒がオンラインでつながる合同ワークショップの様子" loading="lazy" />
                <span className="badge">月1回の合同ワークショップ</span>
              </div>
              <div className="entrebu-body">
                <p>アントレ部は、全国の生徒とつながり、実践・挑戦の場を広げる学校横断の部活動。MiraPASSの標準プランに含まれており、追加費用なく参加できます。</p>
                <div className="pts">
                  <div className="pt"><span className="ic"><CheckIcon size={12} color="#0a6cf5" /></span>全国の学校とつながる実践コミュニティ</div>
                  <div className="pt"><span className="ic"><CheckIcon size={12} color="#0a6cf5" /></span>起業家など実践者からの刺激</div>
                  <div className="pt"><span className="ic"><CheckIcon size={12} color="#0a6cf5" /></span>挑戦の成果が、進路にもつながる</div>
                </div>
                <a href="/entre" className="btn btn-quiet">アントレ部について詳しく見る →</a>
              </div>
            </div>
          </div>
        </section>

        {/* ============ PROOF：導入実績 ============ */}
        <section className="proof" id="proof">
          <div className="wrap">
            <div className="center reveal">
              <p className="sec-kicker">導入実績</p>
              <h2 className="sec-title">行政から私立中高まで、<br />全国で広がっています。</h2>
            </div>
          </div>
          <div className="ticker reveal" aria-hidden="true">
            <div className="track">
              {[...TICKER_1, ...TICKER_1].map(([kind, name], i) => (
                <span className="tk" key={`${name}-${i}`}><small>{kind}</small>{name}</span>
              ))}
            </div>
          </div>
          <div className="ticker t2 reveal" aria-hidden="true">
            <div className="track">
              {[...TICKER_2, ...TICKER_2].map(([kind, name], i) => (
                <span className="tk" key={`${name}-${i}`}><small>{kind}</small>{name}</span>
              ))}
            </div>
          </div>
          <p className="proof-note">これまで29校・2,143名の学校・先生・生徒にサービスを提供（2022年活動開始）</p>
        </section>

        {/* ============ VOICE：導入校の声 ============ */}
        <section className="voice-sec">
          <div className="wrap">
            <div className="center reveal">
              <p className="sec-kicker">導入校の声</p>
              <h2 className="sec-title center">現場の先生が、いちばんの証明です。</h2>
            </div>
            <div className="voice-grid reveal">
              <div className="voice-photo">
                <img src={voiceSchool} alt="かほく市立高松中学校の先生方" loading="lazy" />
                <span className="badge">かほく市立高松中学校（石川県）</span>
              </div>
              <div className="voice-body">
                <span className="q">“</span>
                <p className="main">AIチャット「みらいワーク」は、一斉授業で対応しきれない部分を補い、個別に寄り添ったサポートを提供してくれます。問い返し形式で生徒の思考を広げ、深めることができるため、「自分で考える力」を育てる構成に安心して使用させています。</p>
                <p className="who">かほく市立高松中学校（石川県）の先生より</p>
                <div className="sub">
                  <div>教員だけでは把握しにくい社会のニーズや市場の動向を知ることができ、授業づくりのヒントを多く得られました。</div>
                  <div>教育課程や生徒の実態に合わせて資料映像やワークシートを作成いただけるため、準備の負担が減り、働き方改革の面でも助かっています。</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ PRICE ============ */}
        <section className="sec price" id="price">
          <div className="wrap">
            <div className="reveal">
              <p className="sec-kicker">料金プラン</p>
              <h2 className="sec-title center">月550円で、すべての機能を。</h2>
              <div className="amount"><span className="yen">¥</span>550<span className="per"> ／月・生徒1人（税込）</span></div>
              <p className="model">動画・AIみらいワーク・先生向け可視化・アントレ部まで、全機能が利用可能な教材費モデル。</p>
              <div className="pills">
                <span className="pill">先生アカウント <b>無料</b></span>
                <span className="pill">アントレ部 <b>標準プランに含む</b></span>
                <span className="pill">オリジナル教材制作 <b>オプション</b></span>
              </div>
              <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">詳しい料金を資料で確認する</a>
              <p className="fine">※ 導入形態（コマ数・学年規模）によりご提案内容が変わります。正式なお見積りは資料請求・ご相談時にご案内します。</p>
            </div>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section className="sec faq-sec" id="faq">
          <div className="wrap">
            <div className="center reveal">
              <p className="sec-kicker">よくあるご質問</p>
              <h2 className="sec-title center">FAQ</h2>
            </div>
            <div className="faq-list reveal">
              {FAQ.map(item => (
                <details className="qa" key={item.q}>
                  <summary><span>{item.q}</span><span className="plus" aria-hidden="true"></span></summary>
                  <div className="ans">{item.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ============ SCENE 2：締めの全幅写真 ============ */}
        <section className="scene" aria-label="導入校の授業風景">
          <img src={galClassroom} alt="かほく市立高松中学校の授業。スクリーンと手元の端末にMiraPASSが映る" loading="lazy" data-parallax />
          <div className="veil" aria-hidden="true" />
          <div className="cap">
            <div className="wrap">
              <span className="chip2">かほく市立高松中学校</span>
              <h3>教室の「わかった」が、未来を変える。</h3>
            </div>
          </div>
        </section>

        {/* ============ CTA ============ */}
        <section className="cta" id="contact">
          <div className="aurora" aria-hidden="true" />
          <div className="grain" aria-hidden="true" />
          <div className="wrap">
            <div className="center reveal">
              <p className="sec-kicker" style={{ color: '#8e8e93' }}>資料請求・お問い合わせ</p>
              <h2 className="sec-title center">まずは、3分でわかる資料から。</h2>
              <p className="sec-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>プロダクト・カリキュラム・導入事例・料金の詳細をまとめた資料をお送りします。導入のご相談だけでも歓迎です。</p>
              <div style={{ marginTop: 38 }}>
                <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ padding: '18px 44px', fontSize: '1.02rem' }}>資料請求・お問い合わせはこちら</a>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer>
          <div className="wrap">
            <div className="inner">
              <div className="foot-top">
                <div>
                  <a href="https://mirasso.co.jp/" target="_blank" rel="noopener noreferrer" className="logo">
                    <img src={mirapassLogo} alt="MiraPASS" className="logo-img" />
                  </a>
                  <p className="foot-desc"><b>ミラッソ株式会社</b>が運営する、AI探究プラットフォーム。動画とAI「みらいワーク」で、生徒一人ひとりの探究に伴走します。</p>
                </div>
                <div className="foot-links">
                  <div className="foot-col">
                    <h5>MiraPASS</h5>
                    <a href="#product">プロダクト</a>
                    <a href="#teacher">先生向け</a>
                    <a href="#curriculum">カリキュラム</a>
                    <a href="#price">料金</a>
                    <a href="/entre">アントレ部</a>
                  </div>
                  <div className="foot-col">
                    <h5>会社</h5>
                    <a href={COMPANY_URL} target="_blank" rel="noopener noreferrer">会社概要</a>
                    <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">お問い合わせ</a>
                    <button type="button" onClick={() => setIsTermsDialogOpen(true)}>利用規約</button>
                  </div>
                  <div className="foot-col">
                    <h5>アカウント</h5>
                    <button type="button" onClick={() => setIsLoginDialogOpen(true)}>ログイン</button>
                  </div>
                </div>
              </div>
              <div className="foot-bottom">
                <span>© 2025 MiraPASS All Rights Reserved.</span>
                <span>ミラッソ株式会社{'　'}東京都渋谷区神宮前6-23-4 桑野ビル2階</span>
              </div>
            </div>
          </div>
        </footer>

        {/* スマホ固定CTA */}
        <div className="sticky-cta">
          <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">資料請求はこちら（無料）</a>
        </div>
      </div>

      <LoginDialog isOpen={isLoginDialogOpen} onClose={() => setIsLoginDialogOpen(false)} />
      <TermsDialog isOpen={isTermsDialogOpen} onClose={() => setIsTermsDialogOpen(false)} />

      {googleClientId && (
        <GoogleOneTap
          clientId={googleClientId}
          onSuccess={() => console.log('🟢 Google One Tap successful')}
          onError={(error) => console.error('🔴 Google One Tap error:', error)}
        />
      )}
    </>
  )
}

export default LandingPage
